import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import Header from '../../components/Header';
import AuthHOC from '../Auth';
import { withAlert } from 'react-alert';
import { v4 as uuidv4 } from 'uuid';
import {
    Box,
    Paper,
    Typography,
    Stack,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Alert,
    CircularProgress
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import InfoIcon from '@mui/icons-material/Info';
import ArchiveIcon from '@mui/icons-material/Archive';
import SyncIcon from '@mui/icons-material/Sync';
import RouterIcon from '@mui/icons-material/Router';
import PrintIcon from '@mui/icons-material/Print';
import CableIcon from '@mui/icons-material/Cable';
import ApiIcon from '@mui/icons-material/Api';

import { getConfig, setConfigSettings, getAboutInfo, getSmtpConfig, enableMultiSmtp, archiveNow, archiveImmediately, purgeColdArchive, deleteColdArchive } from '../../services/configurationService';
import PrintTokensTab from '../../components/printing/PrintTokensTab';
import { getX3Folders, testX3Connection } from '../../services/x3Service';
import { getAll as getAllUsers } from '../../services/userService';
import ConfirmDeleteModal from '../../components/modals/common/ConfirmDeleteModal';
import ConfirmModal from '../../components/modals/common/ConfirmModal';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';

// Import section components
import SystemConfigSection from './SystemConfigSection';
import SageConfigSection from './SageConfigSection';
import LicenseConfigSection from './LicenseConfigSection';
import EmailConfigSection from './EmailConfigSection';
import AboutSection from './AboutSection';
import EventViewerArchiveConfigSection from './EventViewerArchiveConfigSection';
import PollerConfigSection from './PollerConfigSection';
import ProxyConfigSection from './ProxyConfigSection';
import ExternalApiConfigSection from './ExternalApiConfigSection';


// Empty email form template
function getEmptyEmailForm() {
    return {
        settingsid: '',
        emailConfig: '',
        type: '',
        host: '',
        port: '',
        folder: '',
        username: '',
        password: '',
        emailFrom: '',
        replyTo: '',
        admin_address: '',
        secure: false,
        ignoreTLS: false,
        requireTLS: false,
        tlsServerName: '',
        isDefault: false,
        tenantId: '',
        clientId: '',
        clientSecret: '',
        isSystemNotification: false,
        adminAddresses: [],
    };
}

// Navigation items configuration
const NAV_ITEMS = [
    { id: 'system', label: 'System', icon: <SettingsIcon sx={{ color: '#607d8b' }} /> },
    { id: 'sage', label: 'Sage X3', icon: <StorageIcon sx={{ color: '#43a047' }} /> },
    { id: 'eventviewerarchive', label: 'Event Viewer Archive', icon: <ArchiveIcon sx={{ color: '#78909c' }} /> },
    { id: 'pollerSplitFilename', label: 'Poller', icon: <SyncIcon sx={{ color: '#8e24aa' }} /> },
    { id: 'smtp', label: 'Email', icon: <EmailIcon sx={{ color: '#1e88e5' }} /> },
    { id: 'license', label: 'License', icon: <KeyIcon sx={{ color: '#f9a825' }} /> },
    { id: 'proxy', label: 'Proxy', icon: <RouterIcon sx={{ color: '#8e44ad' }} /> },
    { id: 'print_tokens', label: 'Websocket Tokens', icon: <CableIcon sx={{ color: '#e67e22' }} /> },
    { id: 'external', label: 'Integration APIs', icon: <ApiIcon sx={{ color: '#00897b' }} /> },
    { id: 'divider1', type: 'divider' },
    { id: 'about', label: 'About', icon: <InfoIcon sx={{ color: '#7443f4' }} /> },
];

// Setup mode tabs — only Sage X3, License, and Proxy are available until Sage is configured
const SETUP_MODE_TABS = ['sage', 'license', 'proxy'];

function ConfigurationView(props) {
    const alertRef = useRef(null);

    // General state
    const [config, setConfig] = useState(null);
    const [aboutInfo, setAboutInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [selectedSection, setSelectedSection] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        const validIds = NAV_ITEMS.filter(item => item.type !== 'divider').map(item => item.id);
        return validIds.includes(tab) ? tab : 'system';
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [originalConfig, setOriginalConfig] = useState(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingSection, setPendingSection] = useState(null);

    // Setup mode state
    const [isSetupMode, setIsSetupMode] = useState(false);
    const [isRestarting, setIsRestarting] = useState(false);
    const [restartFailed, setRestartFailed] = useState(false);
    const [sageMigrationRetry, setSageMigrationRetry] = useState(false);

    // Email-specific state
    const [smtpConfig, setSmtpConfig] = useState(null);
    const [smtpSettings, setSmtpSettings] = useState([]);
    const [x3Folders, setX3Folders] = useState([]);
    const [selectedSmtpIndex, setSelectedSmtpIndex] = useState(null);
    const [smtpFormData, setSmtpFormData] = useState(getEmptyEmailForm());
    const [smtpFormHasChanges, setSmtpFormHasChanges] = useState(false);
    const [smtpSaving, setSmtpSaving] = useState(false);
    const [smtpTestLoading, setSmtpTestLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [smtpToDelete, setSmtpToDelete] = useState(null);
    const [smtpDeleting, setSmtpDeleting] = useState(false);

    // Sage X3 connection test state
    const [sageConnectionStatus, setSageConnectionStatus] = useState({ loading: false });
    const [sageSaving, setSageSaving] = useState(false);

    // Notification recipients state (WI-10558) — emails of active admin users for the dropdown
    const [adminUserEmails, setAdminUserEmails] = useState([]);

    // Event viewer archive state
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [archiveImmediatelyLoading, setArchiveImmediatelyLoading] = useState(false);
    const [purgeArchiveLoading, setPurgeArchiveLoading] = useState(false);
    const [deleteArchiveLoading, setDeleteArchiveLoading] = useState(false);

    const { parsed, userReducer } = props;

    const localisation = useMemo(() => {
        return userReducer?.localisation || parsed?.localisation || {};
    }, [userReducer, parsed]);

    const getTitle = useCallback(() => {
        return localisation["system_configuration_header"]?.element_lang || "System Configuration";
    }, [localisation]);

    // Load X3 folders separately (non-blocking)
    const loadX3Folders = useCallback(async () => {
        try {
            const x3FoldersResult = await getX3Folders();
            if (x3FoldersResult.ok && x3FoldersResult.data) {
                setX3Folders(x3FoldersResult.data);
            }
        } catch (err) {
            console.error('Failed to load X3 folders:', err);
            setError('Failed to load X3 folders. Please configure X3 settings first.');
        }
    }, []);

    // Load configuration data
    const loadConfig = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const [configResult, aboutResult, smtpResult] = await Promise.all([
                getConfig(),
                getAboutInfo(),
                getSmtpConfig()
            ]);

            if (configResult.ok) {
                setConfig(configResult.data);
                setOriginalConfig(JSON.parse(JSON.stringify(configResult.data)));
            } else {
                setError('Failed to load configuration');
            }

            if (aboutResult.ok) {
                setAboutInfo(aboutResult.data);
            }

            if (smtpResult.ok && smtpResult.data) {
                setSmtpConfig(smtpResult.data);
                setSmtpSettings(smtpResult.data.settings || []);
            }

            // Load X3 folders in the background (non-blocking)
            loadX3Folders();
        } catch (err) {
            setError('Failed to load configuration: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    }, [loadX3Folders]);

    const loadSmtpConfig = useCallback(async () => {
        try {
            const smtpResult = await getSmtpConfig();
            if (smtpResult.ok && smtpResult.data) {
                setSmtpConfig(smtpResult.data);
                setSmtpSettings(smtpResult.data.settings || []);
            }
        } catch (err) {
            console.error('Failed to load SMTP config:', err);
        }
    }, []);

    const checkSetupStatus = useCallback(async () => {
        try {
            const [setupRes, licenseRes] = await Promise.all([
                fetch('/api/config/setup-status'),
                fetch('/api/license/status'),
            ]);
            const setupData = await setupRes.json();
            const licenseData = await licenseRes.json();

            const unlicensed = !licenseData.isValid;
            if (setupData.migrationPending || unlicensed) {
                setIsSetupMode(true);
                setSelectedSection(setupData.migrationPending ? 'sage' : 'license');
                // Sage is configured but migration still pending = wrong Sage details (not just unlicensed)
                if (setupData.migrationPending && setupData.isSageConfigured) {
                    setSageMigrationRetry(true);
                }
            } else {
                setIsSetupMode(false);
            }
        } catch (err) {
            console.error('Failed to check setup status:', err);
        }
    }, []);

    // Check setup status on mount
    useEffect(() => {
        checkSetupStatus();
    }, [checkSetupStatus]);

    // Load active admin users' emails for the Email section's system notification
    // recipients dropdown (WI-11437). Non-blocking; a 403 (non-admin caller) just
    // leaves the dropdown empty.
    useEffect(() => {
        (async () => {
            try {
                const result = await getAllUsers({ isActive: true });
                if (result.ok && Array.isArray(result.data)) {
                    const emails = result.data
                        .filter(u => u.type === 'admin' && u.email)
                        .map(u => u.email);
                    setAdminUserEmails([...new Set(emails)]);
                }
            } catch (err) {
                console.error('Failed to load admin users for notifications:', err);
            }
        })();
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    // Auto-dismiss success message
    useEffect(() => {
        if (successMsg) {
            const timer = setTimeout(() => setSuccessMsg(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [successMsg]);

    // Scroll to alert area when error or success message appears
    useEffect(() => {
        if ((error || successMsg) && alertRef.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error, successMsg]);

    // Determine if current section has unsaved changes
    const currentSectionIsDirty = () => {
        if (selectedSection === 'smtp') return smtpFormHasChanges;
        if (selectedSection === 'about') return false;
        if (selectedSection === 'print_tokens') return false;
        return hasChanges;
    };

    // Bug 11305: the modal above only covered switching Configuration's own
    // tabs. This also holds any navigation away from the page (main nav,
    // back button) while there are unsaved changes, and history registers
    // the browser's leave-page prompt for closing/reloading the tab.
    const { isLeaving, confirmLeave, cancelLeave } = useUnsavedChangesGuard(
        props.history,
        currentSectionIsDirty()
    );

    // Section change handler
    const handleSectionChange = (sectionId) => {
        if (currentSectionIsDirty()) {
            setPendingSection(sectionId);
            setShowUnsavedModal(true);
            return;
        }
        setSelectedSection(sectionId);
        setSageConnectionStatus({ loading: false });
    };

    // Discard changes: revert current section to original
    const discardCurrentSectionChanges = () => {
        if (selectedSection === 'smtp') {
            if (selectedSmtpIndex !== null) {
                setSmtpFormData({ ...smtpSettings[selectedSmtpIndex] });
            } else {
                setSmtpFormData(getEmptyEmailForm());
            }
            setSmtpFormHasChanges(false);
        } else {
            if (selectedSection && originalConfig) {
                const configKey = selectedSection === 'proxy' ? 'license' : selectedSection;
                setConfig(prev => ({
                    ...prev,
                    [configKey]: JSON.parse(JSON.stringify(originalConfig[configKey] || {}))
                }));
            }
        }

        setHasChanges(false);
    };

    // Unsaved changes modal handlers - switching Configuration tabs
    const handleUnsavedConfirm = () => {
        discardCurrentSectionChanges();
        setShowUnsavedModal(false);
        setSelectedSection(pendingSection);
        setPendingSection(null);
    };

    const handleUnsavedCancel = () => {
        setShowUnsavedModal(false);
        setPendingSection(null);
    };

    // Unsaved changes modal handlers - leaving the page (Bug 11305)
    const handleLeaveConfirm = () => {
        discardCurrentSectionChanges();
        confirmLeave();
    };

    // Generic config change handler
    const handleInputChange = (category, key, value) => {
        setConfig(prev => ({
            ...prev,
            [category]: {
                ...(prev?.[category] || {}),
                [key]: value
            }
        }));
        setHasChanges(true);
        if (category === 'sage') {
            setSageConnectionStatus({ loading: false });
        }
    };

    // Generic save handler
    const handleSave = async (category) => {
        try {
            const currentSettings = config?.[category] || {};
            const result = await setConfigSettings(category, { [category]: currentSettings });
            if (result.ok) {
                setSuccessMsg(`${category.charAt(0).toUpperCase() + category.slice(1)} settings saved successfully`);
                setHasChanges(false);
                // Reload config for categories where server calculates additional values or to ensure sync with database
                if (category === 'eventviewerarchive' || category === 'system') {
                    await loadConfig();
                } else {
                    setOriginalConfig(prev => ({
                        ...prev,
                        [category]: JSON.parse(JSON.stringify(currentSettings))
                    }));
                }
                if (result.data?.restart) {
                    props.alert.info('An application restart is required');
                }

                // If Sage was saved in setup mode and server is restarting, show overlay
                if (result.data?.setupComplete) {
                    setIsRestarting(true);
                    setRestartFailed(false);
                    // Poll until server is back, then reload
                    const pollInterval = setInterval(async () => {
                        try {
                            const res = await fetch('/api/config/setup-status');
                            if (res.ok) {
                                clearInterval(pollInterval);
                                window.location.href = '/auth/basic';
                            }
                        } catch (_) {
                            // Server still restarting, keep polling
                        }
                    }, 3000);
                    // If server hasn't come back after 30 seconds, show manual restart message
                    setTimeout(() => {
                        clearInterval(pollInterval);
                        setRestartFailed(true);
                    }, 30000);
                }
            } else {
                setError('Failed to save settings');
            }
        } catch (err) {
            setError('Failed to save settings: ' + err.message);
        }
    };

    // Sage X3 connection test handler — sends current form values so it works before saving
    const handleTestX3Connection = async () => {
        setSageConnectionStatus({ loading: true });
        setError('');
        setSuccessMsg('');
        try {
            const result = await testX3Connection(config?.sage || {});
            if (result.data?.success) {
                setSuccessMsg(result.data.message);
            } else {
                setError(result.data?.error || 'Connection test failed.');
            }
        } catch (err) {
            setError('Connection test failed: ' + err.message);
        } finally {
            setSageConnectionStatus({ loading: false });
        }
    };

    // Sage X3 save handler — runs connection test first, only saves if test passes
    const handleSaveSage = async () => {
        setSageSaving(true);
        setError('');
        setSuccessMsg('');
        try {
            const result = await testX3Connection(config?.sage || {});
            if (!result.data?.success) {
                setError(result.data?.error || 'Connection test failed. Settings were not saved.');
                return;
            }
            await handleSave('sage');
            setSuccessMsg('Sage X3 settings saved. Connection verified successfully.');
        } catch (err) {
            setError('Connection test failed: ' + err.message + '. Settings were not saved.');
        } finally {
            setSageSaving(false);
        }
    };

    // Email section handlers
    const handleEnableMultiSmtp = async () => {
        try {
            const result = await enableMultiSmtp();
            if (result.ok) {
                setSuccessMsg('Email configuration enabled successfully');
                await loadSmtpConfig();
            } else {
                setError('Failed to enable email configuration');
            }
        } catch (err) {
            setError('Failed to enable email configuration: ' + err.message);
        }
    };

    // Event viewer archive handlers
    const handleArchiveNow = async () => {
        setArchiveLoading(true);
        try {
            const result = await archiveNow();
            if (result.ok) {
                setSuccessMsg(`Archive complete. ${result.data?.archivedCount || 0} records older than configured threshold were archived.`);
                await loadConfig();
            } else {
                setError(result.data?.message || 'Archive failed');
            }
        } catch (err) {
            setError('Archive failed: ' + err.message);
        } finally {
            setArchiveLoading(false);
        }
    };

    const handleArchiveImmediately = async () => {
        setArchiveImmediatelyLoading(true);
        try {
            const result = await archiveImmediately();
            if (result.ok) {
                setSuccessMsg(`Immediate archive complete. ${result.data?.archivedCount || 0} records archived (all records regardless of date).`);
                await loadConfig();
            } else {
                setError(result.data?.message || 'Immediate archive failed');
            }
        } catch (err) {
            setError('Immediate archive failed: ' + err.message);
        } finally {
            setArchiveImmediatelyLoading(false);
        }
    };

    const handlePurgeColdArchive = async () => {
        setPurgeArchiveLoading(true);
        try {
            const result = await purgeColdArchive();
            if (result.ok) {
                setSuccessMsg(`Purge complete. ${result.data?.deletedCount || 0} archived records older than retention period were permanently deleted.`);
                await loadConfig();
            } else {
                setError(result.data?.message || 'Purge failed');
            }
        } catch (err) {
            setError('Purge failed: ' + err.message);
        } finally {
            setPurgeArchiveLoading(false);
        }
    };

    const handleDeleteColdArchive = async () => {
        setDeleteArchiveLoading(true);
        try {
            const result = await deleteColdArchive();
            if (result.ok) {
                setSuccessMsg(`Cold archive deleted. ${result.data?.deletedCount || 0} records removed.`);
            } else {
                setError('Failed to delete cold archive');
            }
        } catch (err) {
            setError('Failed to delete cold archive: ' + err.message);
        } finally {
            setDeleteArchiveLoading(false);
        }
    };

    const handleSmtpFormChange = (field, value) => {
        setSmtpFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-fill for server type changes
            if (field === 'type') {
                if (value === 'Office365') {
                    updated.host = 'smtp.office365.com';
                    updated.port = '587';
                    updated.secure = false;
                    updated.tenantId = '';
                    updated.clientId = '';
                    updated.clientSecret = '';
                } else if (value === 'SMTP') {
                    updated.tenantId = '';
                    updated.clientId = '';
                    updated.clientSecret = '';
                } else if (value === 'MicrosoftGraph') {
                    updated.host = '';
                    updated.port = '';
                    updated.secure = false;
                    updated.ignoreTLS = false;
                    updated.requireTLS = false;
                }
            }

            return updated;
        });
        setSmtpFormHasChanges(true);
    };

    const handleSelectSmtpConfig = (index) => {
        if (index === null) {
            setSelectedSmtpIndex(null);
            setSmtpFormData(getEmptyEmailForm());
        } else {
            setSelectedSmtpIndex(index);
            setSmtpFormData({ ...smtpSettings[index] });
        }
        setSmtpFormHasChanges(false);
    };

    const handleSmtpSave = async () => {
        const currentIndex = selectedSmtpIndex ?? smtpSettings.length;
        const newName = (smtpFormData.emailConfig || '').trim().toLowerCase();
        const newFolder = (smtpFormData.folder || '').trim().toLowerCase();

        // Rule 1: Unique emailConfig name + folder combination
        const duplicateNameFolder = smtpSettings.findIndex((s, i) =>
            i !== currentIndex &&
            (s.emailConfig || '').trim().toLowerCase() === newName &&
            (s.folder || '').trim().toLowerCase() === newFolder
        );
        if (duplicateNameFolder !== -1) {
            setError(`An email configuration named "${smtpFormData.emailConfig}" already exists for folder "${smtpFormData.folder}".`);
            return;
        }

        // Rule 2: Only one default per folder
        if (smtpFormData.isDefault) {
            const existingDefault = smtpSettings.findIndex((s, i) =>
                i !== currentIndex &&
                s.isDefault &&
                (s.folder || '').trim().toLowerCase() === newFolder
            );
            if (existingDefault !== -1) {
                const existingName = smtpSettings[existingDefault].emailConfig || 'Unnamed';
                setError(`Folder "${smtpFormData.folder}" already has a default configuration ("${existingName}"). Please remove the existing default first.`);
                return;
            }
        }

        // WI-11437: Rule 3 — only one System Notification configuration system-wide
        if (smtpFormData.isSystemNotification) {
            const existingNotification = smtpSettings.findIndex((s, i) =>
                i !== currentIndex && s.isSystemNotification
            );
            if (existingNotification !== -1) {
                const existingName = smtpSettings[existingNotification].emailConfig || 'Unnamed';
                setError(`"${existingName}" is already the System Notification configuration. Only one configuration can send system notifications — remove the flag there first.`);
                return;
            }
        }

        setSmtpSaving(true);
        try {
            const settings = {
                ...smtpFormData,
                settingsid: smtpFormData.settingsid || uuidv4()
            };

            const response = await fetch(`/api/config/set_db_config?id=${smtpConfig._id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    settings,
                    id: currentIndex
                })
            });

            if (response.ok) {
                setSuccessMsg(selectedSmtpIndex !== null ? 'Email configuration updated' : 'Email configuration added');
                await loadSmtpConfig();
                setSmtpFormHasChanges(false);
                if (selectedSmtpIndex === null) {
                    setSelectedSmtpIndex(smtpSettings.length);
                }
            } else {
                const result = await response.json().catch(() => ({}));
                setError(result.error || 'Failed to save email configuration');
            }
        } catch (err) {
            setError('Failed to save: ' + err.message);
        } finally {
            setSmtpSaving(false);
        }
    };

    const handleSmtpDeleteClick = (index) => {
        setSmtpToDelete(index);
        setDeleteConfirmOpen(true);
    };

    const handleSmtpDeleteConfirm = async () => {
        if (smtpToDelete === null) return;

        setSmtpDeleting(true);
        try {
            const response = await fetch(`/api/config/set_db_config?id=${smtpConfig._id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settingsId: smtpToDelete })
            });

            if (response.ok) {
                setSuccessMsg('Email configuration deleted');
                await loadSmtpConfig();
                if (selectedSmtpIndex === smtpToDelete) {
                    setSelectedSmtpIndex(null);
                    setSmtpFormData(getEmptyEmailForm());
                }
                setDeleteConfirmOpen(false);
                setSmtpToDelete(null);
            } else {
                // WI-11437: surface the API's reason (e.g. the System Notification delete guard).
                // Bug #11848: close the confirmation first - it used to stay open with the
                // reason hidden in the page banner behind it, so Delete looked broken.
                const result = await response.json().catch(() => ({}));
                setDeleteConfirmOpen(false);
                setSmtpToDelete(null);
                setError(result.error || 'Failed to delete email configuration');
            }
        } catch (err) {
            setDeleteConfirmOpen(false);
            setSmtpToDelete(null);
            setError('Failed to delete: ' + err.message);
        } finally {
            setSmtpDeleting(false);
        }
    };

    const handleSmtpDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setSmtpToDelete(null);
    };

    const handleSmtpTest = async () => {
        setSmtpTestLoading(true);
        try {
            const type = smtpFormData.type?.toLowerCase();
            // WI-11437: a System Notification config carries a recipient list instead
            // of a single admin address — test emails go to the whole list.
            const testAdminAddress = smtpFormData.isSystemNotification
                ? (smtpFormData.adminAddresses || []).join(', ')
                : smtpFormData.admin_address;
            const commonParams = {
                type,
                fromAddress: smtpFormData.emailFrom,
                replyToAddress: smtpFormData.replyTo,
                adminAddress: testAdminAddress,
                username: smtpFormData.username
            };

            let emailParams = {};
            if (type === 'smtp' || type === 'office365') {
                emailParams = {
                    ...commonParams,
                    server: smtpFormData.host,
                    port: smtpFormData.port,
                    host: smtpFormData.host,
                    secure: smtpFormData.secure,
                    ignoreTls: smtpFormData.ignoreTLS,
                    requireTls: smtpFormData.requireTLS,
                    password: smtpFormData.password,
                    bypassProxy: smtpFormData.bypassProxy || false
                };
            } else if (type === 'microsoftgraph') {
                emailParams = {
                    ...commonParams,
                    tenantId: smtpFormData.tenantId,
                    clientId: smtpFormData.clientId,
                    clientSecret: smtpFormData.clientSecret
                };
            } else if (type === 'sendgrid') {
                emailParams = {
                    type,
                    emailFrom: smtpFormData.emailFrom,
                    emailConfig: smtpFormData.emailConfig,
                    folder: smtpFormData.folder,
                    adminAddress: testAdminAddress,
                    apiKey: smtpFormData.apiKey
                };
            }

            const response = await fetch('/api/email/send_test_email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(emailParams)
            });

            const result = await response.json();
            if (result.error) {
                setError('Test email failed: ' + result.message);
            } else {
                setSuccessMsg('Test email sent successfully');
            }
        } catch (err) {
            setError('Test failed: ' + err.message);
        } finally {
            setSmtpTestLoading(false);
        }
    };

    const isSmtpFormValid = () => {
        const { type, folder, emailFrom, admin_address, host, port, tenantId, clientId, clientSecret, username, apiKey, isSystemNotification, adminAddresses } = smtpFormData;
        // WI-11437: a System Notification config needs at least one admin recipient
        // in its list; other configs need the single Admin Address.
        const hasAdminRecipient = isSystemNotification
            ? Array.isArray(adminAddresses) && adminAddresses.length > 0
            : !!admin_address;
        if (!type || !folder || !emailFrom || !hasAdminRecipient) return false;

        if (type === 'MicrosoftGraph') {
            return !!(tenantId && clientId && clientSecret && username);
        } else if (type === 'SendGrid') {
            return !!apiKey;
        } else {
            return !!(host && port);
        }
    };

    // Render main content based on selected section
    const renderMainContent = () => {
        switch (selectedSection) {
            case 'system':
                return (
                    <SystemConfigSection
                        config={config?.system || {}}
                        onChange={(key, value) => handleInputChange('system', key, value)}
                        onSave={() => handleSave('system')}
                        hasChanges={hasChanges}
                        localisation={localisation}
                        deploymentType={aboutInfo?.deploymentType || 'onprem'}
                    />
                );

            case 'sage':
                return (
                    <SageConfigSection
                        config={config?.sage || {}}
                        onChange={(key, value) => handleInputChange('sage', key, value)}
                        onSave={handleSaveSage}
                        hasChanges={hasChanges}
                        localisation={localisation}
                        onTestConnection={handleTestX3Connection}
                        connectionStatus={sageConnectionStatus}
                        isSaving={sageSaving}
                    />
                );

            case 'license':
                return (
                    <LicenseConfigSection
                        config={config?.license || {}}
                        onChange={(key, value) => handleInputChange('license', key, value)}
                        onSave={() => handleSave('license')}
                        hasChanges={hasChanges}
                        localisation={localisation}
                    />
                );

            case 'eventviewerarchive':
                return (
                    <EventViewerArchiveConfigSection
                        config={config?.eventviewerarchive || {}}
                        onChange={(key, value) => handleInputChange('eventviewerarchive', key, value)}
                        onSave={() => handleSave('eventviewerarchive')}
                        hasChanges={hasChanges}
                        localisation={localisation}
                        onArchiveNow={handleArchiveNow}
                        onArchiveImmediately={handleArchiveImmediately}
                        onPurgeColdArchive={handlePurgeColdArchive}
                        onDeleteColdArchive={handleDeleteColdArchive}
                        archiveLoading={archiveLoading}
                        archiveImmediatelyLoading={archiveImmediatelyLoading}
                        purgeLoading={purgeArchiveLoading}
                        deleteLoading={deleteArchiveLoading}
                    />
                );

            case 'pollerSplitFilename':
                return (
                    <PollerConfigSection
                        config={config?.pollerSplitFilename || {}}
                        onChange={(key, value) => handleInputChange('pollerSplitFilename', key, value)}
                        onSave={() => handleSave('pollerSplitFilename')}
                        hasChanges={hasChanges}
                        localisation={localisation}
                    />
                );

            case 'smtp':
                return (
                    <EmailConfigSection
                        smtpConfig={smtpConfig}
                        smtpSettings={smtpSettings}
                        selectedIndex={selectedSmtpIndex}
                        formData={smtpFormData}
                        formHasChanges={smtpFormHasChanges}
                        saving={smtpSaving}
                        testLoading={smtpTestLoading}
                        hasSageConfig={!!(config?.sage?.address && config?.sage?.folder)}
                        x3Folders={x3Folders}
                        onFormChange={handleSmtpFormChange}
                        onSelectConfig={handleSelectSmtpConfig}
                        onSave={handleSmtpSave}
                        onDeleteClick={handleSmtpDeleteClick}
                        onTest={handleSmtpTest}
                        onEnableMultiSmtp={handleEnableMultiSmtp}
                        isFormValid={isSmtpFormValid()}
                        deploymentType={aboutInfo?.deploymentType || 'onprem'}
                        adminUserEmails={adminUserEmails}
                    />
                );

            case 'proxy':
                return (
                    <ProxyConfigSection
                        config={config?.license || {}}
                        onChange={(key, value) => handleInputChange('license', key, value)}
                        onSave={() => handleSave('license')}
                        hasChanges={hasChanges}
                        localisation={localisation}
                    />
                );

            case 'external':
                return (
                    <ExternalApiConfigSection
                        config={config?.external || {}}
                        onChange={(key, value) => handleInputChange('external', key, value)}
                        onSave={() => handleSave('external')}
                        hasChanges={hasChanges}
                        localisation={localisation}
                    />
                );

            case 'print_tokens':
                return <PrintTokensTab />;

            case 'about':
                return <AboutSection aboutInfo={aboutInfo} />;

            default:
                return (
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography color="text.secondary">
                            Select a category from the menu.
                        </Typography>
                    </Box>
                );
        }
    };

    if (isLoading) {
        return (
            <div className="App">
                <Header visible={!props.parsed?.X3} />
                <div className="page-wrapper">
                    <div className="s-page">
                        <div className="s-page-header">
                            <Stack spacing={0.5} alignItems="flex-start" sx={{ mb: 3, textAlign: "left" }}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    {getTitle()}
                                </Typography>
                            </Stack>
                        </div>
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress />
                        </Box>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <Header visible={!props.parsed?.X3} />
            <div className="page-wrapper">
                <div className="s-page">
                    <div className="s-page-header">
                        <Stack spacing={0.5} alignItems="flex-start" sx={{ mb: 3, textAlign: "left" }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {getTitle()}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Manage application settings and integrations
                            </Typography>
                        </Stack>
                    </div>

                <div ref={alertRef}>
                {isSetupMode && (
                    <Alert severity={sageMigrationRetry ? "warning" : "info"} sx={{ mb: 2 }}>
                        {sageMigrationRetry
                            ? "Failed to connect to Sage X3 with the current settings. Please verify your Sage X3 connection details (address, folder, credentials) and save again."
                            : "Please configure your Sage X3 connection details to complete the application setup. The application will restart automatically once the configuration is saved."
                        }
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
                        {successMsg}
                    </Alert>
                )}
                </div>

                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    {/* Sidebar Navigation */}
                    <Paper
                        elevation={1}
                        sx={{
                            width: 240,
                            flexShrink: 0,
                            borderTop: '4px solid #7443f4',
                        }}
                    >
                        <List component="nav" sx={{ py: 1 }}>
                            {NAV_ITEMS
                                .filter((item) => {
                                    if (!isSetupMode) return true;
                                    // In setup mode, only show Sage X3 and License tabs
                                    return item.type === 'divider'
                                        ? false
                                        : SETUP_MODE_TABS.includes(item.id);
                                })
                                .map((item) => {
                                if (item.type === 'divider') {
                                    return <Divider key={item.id} sx={{ my: 1 }} />;
                                }

                                return (
                                    <ListItem key={item.id} disablePadding>
                                        <ListItemButton
                                            selected={selectedSection === item.id}
                                            onClick={() => handleSectionChange(item.id)}
                                            sx={{
                                                '&.Mui-selected': {
                                                    backgroundColor: 'rgba(116, 67, 244, 0.08)',
                                                    borderRight: '3px solid #7443f4',
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: 'rgba(116, 67, 244, 0.12)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.label}
                                                primaryTypographyProps={{ fontSize: '0.9rem' }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Paper>

                    {/* Main Content Area */}
                    <Paper
                        elevation={1}
                        sx={{
                            flex: 1,
                            // Flex items default to min-width:auto and refuse to shrink below their
                            // content (e.g. the Websocket Tokens table), which pushed the pane past
                            // the window and made the whole page scroll sideways. minWidth: 0 lets
                            // the pane shrink so wide tables scroll inside their TableContainer.
                            minWidth: 0,
                            p: 3,
                            minHeight: 500,
                            borderTop: '4px solid #7443f4',
                        }}
                    >
                        {renderMainContent()}
                    </Paper>
                </Box>

                {/* Delete Confirmation Modal */}
                <ConfirmDeleteModal
                    isOpen={deleteConfirmOpen}
                    entityType="email configuration"
                    entityName={smtpToDelete !== null ? smtpSettings[smtpToDelete]?.emailConfig || 'Unnamed Config' : ''}
                    onConfirm={handleSmtpDeleteConfirm}
                    onCancel={handleSmtpDeleteCancel}
                    loading={smtpDeleting}
                    message="Are you sure you want to delete this email configuration? Any email functionality using this configuration will stop working."
                />

                <ConfirmModal
                    isOpen={showUnsavedModal || isLeaving}
                    mainTitle="Unsaved changes - Are you absolutely sure?"
                    mainMessage={isLeaving
                        ? "There are unsaved changes, are you sure you want to leave this page? This action cannot be undone."
                        : "There are unsaved changes, are you sure you want to switch tabs? This action cannot be undone."}
                    textConfirm="CONFIRM"
                    textCancel="CANCEL"
                    onConfirm={isLeaving ? handleLeaveConfirm : handleUnsavedConfirm}
                    onCancel={isLeaving ? cancelLeave : handleUnsavedCancel}
                />

                {/* Restart overlay — shown while app restarts after Sage X3 setup */}
                {isRestarting && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}>
                        {!restartFailed ? (
                            <>
                                <CircularProgress sx={{ color: '#fff', mb: 3 }} size={48} />
                                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                                    Restarting application...
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Please wait while the application restarts to complete setup.
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                                    Configuration saved successfully
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, textAlign: 'center', maxWidth: 400 }}>
                                    The application needs to be restarted to complete setup. Please restart the application manually, then refresh this page.
                                </Typography>
                            </>
                        )}
                    </Box>
                )}
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        userReducer: state.userReducer,
        authReducer: state.authReducer,
    };
}

export default connect(mapStateToProps)(
    AuthHOC(withAlert()(ConfigurationView), { name: 'configuration' })
);
